import { api } from './api'
import { featureToggles } from './featureToggles'
import type {
  Allergy,
  AllergyFormData,
  AllergyAuditEvent,
  AllergyAuditEventType,
} from '../types/allergy'

type AllergyCreateInput = AllergyFormData & {
  patientId: string
}

type AllergyUpdateInput = Partial<AllergyFormData>

interface NkdaResult {
  /**
   * The NKDA record after the operation (or null if cleared and no NKDA record remains)
   */
  nkdaAllergy: Allergy | null
}

// ---- Feature toggle helpers ----

function useMockService(): boolean {
  // Default to true in development; toggle can disable mock when backend is ready
  try {
    const useMock = featureToggles.get('allergiesMockService' as any)
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      console.log('[AllergyService] Using mock service:', useMock)
    }
    return useMock
  } catch (error) {
    // If the toggle key is not defined yet, fall back to true
    console.warn('[AllergyService] Error getting feature toggle, defaulting to mock:', error)
    return true
  }
}

// ---- In-memory mock implementation ----

const allergyStore = new Map<string, Allergy[]>() // key: patientId
const allergyIndex = new Map<string, { patientId: string; index: number }>() // key: allergyId

function nowIso(): string {
  return new Date().toISOString()
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `allergy_${Math.random().toString(36).slice(2)}_${Date.now()}`
}

function addAuditEvent(
  allergy: Allergy,
  type: AllergyAuditEventType,
  opts: Partial<Pick<AllergyAuditEvent, 'reason' | 'details' | 'userId' | 'userName'>> = {}
): Allergy {
  const event: AllergyAuditEvent = {
    type,
    timestamp: nowIso(),
    ...opts,
  }
  return {
    ...allergy,
    auditEvents: [...(allergy.auditEvents ?? []), event],
  }
}

function getPatientAllergies(patientId: string): Allergy[] {
  if (!allergyStore.has(patientId)) {
    allergyStore.set(patientId, [])
  }
  return allergyStore.get(patientId)!
}

function upsertAllergyInStore(allergy: Allergy): void {
  const { patientId, id } = allergy
  const list = getPatientAllergies(patientId)
  const indexInfo = allergyIndex.get(id)

  if (indexInfo && indexInfo.patientId === patientId) {
    list[indexInfo.index] = allergy
  } else {
    list.push(allergy)
    allergyIndex.set(id, { patientId, index: list.length - 1 })
  }
}

function findAllergyById(id: string): Allergy | null {
  const ref = allergyIndex.get(id)
  if (!ref) return null
  const list = allergyStore.get(ref.patientId)
  if (!list) return null
  return list[ref.index] ?? null
}

function findNkdaAllergy(patientId: string): Allergy | null {
  const list = getPatientAllergies(patientId)
  return (
    list.find(
      (a) =>
        a.isNKDA &&
        a.triggerType === 'drug' &&
        a.status === 'active'
    ) || null
  )
}

// Seeded example data for local development/demo
function seedDemoDataIfEmpty(patientId: string): void {
  const existing = allergyStore.get(patientId)
  if (existing && existing.length > 0) return

  const now = nowIso()
  const demoAllergy: Allergy = {
    id: generateId(),
    patientId,
    category: 'true-allergy',
    triggerType: 'drug',
    triggerCode: 'AMOX',
    triggerDisplay: 'Amoxicillin',
    reaction: {
      severity: 'moderate',
      description: 'Urticarial rash and lip swelling within 1 hour',
      onset: 'immediate',
      systemInvolved: 'cutaneous',
    },
    recordedDate: now.slice(0, 10),
    status: 'active',
    notes: 'Documented during GP visit in 2024',
    createdAt: now,
    updatedAt: now,
    createdBy: 'system',
    updatedBy: 'system',
    auditEvents: [],
  }

  const withAudit = addAuditEvent(demoAllergy, 'created', {
    userId: 'system',
    userName: 'System',
    details: 'Seeded demo allergy record',
  })

  allergyStore.set(patientId, [withAudit])
  allergyIndex.set(withAudit.id, { patientId, index: 0 })
}

// ---- Mock service implementation ----

async function mockListByPatient(patientId: string): Promise<Allergy[]> {
  if (!patientId) {
    console.warn('[AllergyService] mockListByPatient called without patientId')
    return []
  }
  seedDemoDataIfEmpty(patientId)
  const allergies = [...getPatientAllergies(patientId)]
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    console.log('[AllergyService] mockListByPatient:', { patientId, count: allergies.length })
  }
  return allergies
}

async function mockCreate(input: AllergyCreateInput): Promise<Allergy> {
  const id = generateId()
  const now = nowIso()

  const base: Allergy = {
    id,
    patientId: input.patientId,
    category: input.category,
    triggerType: input.triggerType,
    triggerCode: input.triggerCode,
    triggerDisplay: input.triggerDisplay,
    reaction: input.reaction,
    recordedDate: input.recordedDate,
    status: input.status ?? 'active',
    isNKDA: input.isNKDA,
    notes: input.notes,
    createdAt: now,
    updatedAt: now,
    createdBy: 'mock-user',
    updatedBy: 'mock-user',
    auditEvents: [],
  }

  const withAudit = addAuditEvent(base, 'created', {
    userId: 'mock-user',
    userName: 'Mock User',
    details: base.isNKDA ? 'NKDA record created' : 'Allergy record created',
  })

  upsertAllergyInStore(withAudit)
  return withAudit
}

async function mockUpdate(id: string, data: AllergyUpdateInput): Promise<Allergy> {
  const existing = findAllergyById(id)
  if (!existing) {
    throw new Error('Allergy not found')
  }

  const updated: Allergy = {
    ...existing,
    ...data,
    reaction: data.reaction ?? existing.reaction,
    updatedAt: nowIso(),
  }

  const withAudit = addAuditEvent(updated, 'updated', {
    userId: 'mock-user',
    userName: 'Mock User',
    details: 'Allergy record updated',
  })

  upsertAllergyInStore(withAudit)
  return withAudit
}

async function mockDeactivate(id: string, reason: string): Promise<Allergy> {
  const existing = findAllergyById(id)
  if (!existing) {
    throw new Error('Allergy not found')
  }

  const now = nowIso()
  const updated: Allergy = {
    ...existing,
    status: 'inactive',
    deactivatedAt: now,
    deactivatedBy: 'mock-user',
    deactivationReason: reason,
    updatedAt: now,
  }

  const withAudit = addAuditEvent(updated, 'deactivated', {
    userId: 'mock-user',
    userName: 'Mock User',
    reason,
    details: 'Allergy deactivated',
  })

  upsertAllergyInStore(withAudit)
  return withAudit
}

async function mockSetNkda(patientId: string, flag: boolean, reason?: string): Promise<NkdaResult> {
  const existingNkda = findNkdaAllergy(patientId)

  if (flag) {
    // Setting NKDA
    if (existingNkda) {
      const updated: Allergy = {
        ...existingNkda,
        updatedAt: nowIso(),
      }
      const withAudit = addAuditEvent(updated, 'nkda-set', {
        userId: 'mock-user',
        userName: 'Mock User',
        reason,
        details: 'NKDA reaffirmed',
      })
      upsertAllergyInStore(withAudit)
      return { nkdaAllergy: withAudit }
    }

    const created = await mockCreate({
      patientId,
      category: 'true-allergy',
      triggerType: 'drug',
      triggerDisplay: 'No known drug allergies',
      triggerCode: 'NKDA',
      isNKDA: true,
      recordedDate: nowIso().slice(0, 10),
      notes: reason,
    })

    const withAudit = addAuditEvent(created, 'nkda-set', {
      userId: created.createdBy,
      userName: 'Mock User',
      reason,
      details: 'NKDA set for patient',
    })
    upsertAllergyInStore(withAudit)
    return { nkdaAllergy: withAudit }
  }

  // Clearing NKDA
  if (!existingNkda) {
    return { nkdaAllergy: null }
  }

  const now = nowIso()
  const cleared: Allergy = {
    ...existingNkda,
    status: 'inactive',
    deactivatedAt: now,
    deactivatedBy: 'mock-user',
    deactivationReason: reason ?? 'NKDA cleared',
    updatedAt: now,
  }

  const withAudit = addAuditEvent(cleared, 'nkda-cleared', {
    userId: 'mock-user',
    userName: 'Mock User',
    reason,
    details: 'NKDA cleared for patient',
  })
  upsertAllergyInStore(withAudit)
  return { nkdaAllergy: withAudit }
}

// ---- HTTP implementation (stubbed for future backend) ----

async function httpListByPatient(patientId: string): Promise<Allergy[]> {
  const response = await api.get<Allergy[]>(`/patients/${patientId}/allergies`)
  return response.data
}

async function httpCreate(input: AllergyCreateInput): Promise<Allergy> {
  const { patientId, ...payload } = input
  const response = await api.post<Allergy>(`/patients/${patientId}/allergies`, payload)
  return response.data
}

async function httpUpdate(id: string, data: AllergyUpdateInput): Promise<Allergy> {
  const response = await api.put<Allergy>(`/allergies/${id}`, data)
  return response.data
}

async function httpDeactivate(id: string, reason: string): Promise<Allergy> {
  const response = await api.post<Allergy>(`/allergies/${id}/deactivate`, { reason })
  return response.data
}

async function httpSetNkda(patientId: string, flag: boolean, reason?: string): Promise<NkdaResult> {
  const response = await api.post<Allergy | null>(`/patients/${patientId}/nkda`, {
    nkda: flag,
    reason,
  })
  return { nkdaAllergy: response.data }
}

// ---- Public service API ----

export const allergyService = {
  /**
   * List all allergy/adverse reaction records for a patient.
   */
  listByPatient: async (patientId: string): Promise<Allergy[]> => {
    if (useMockService()) {
      return mockListByPatient(patientId)
    }
    return httpListByPatient(patientId)
  },

  /**
   * Create a new allergy/adverse reaction record for a patient.
   */
  create: async (input: AllergyCreateInput): Promise<Allergy> => {
    if (useMockService()) {
      return mockCreate(input)
    }
    return httpCreate(input)
  },

  /**
   * Update an existing allergy record by id.
   */
  update: async (id: string, data: AllergyUpdateInput): Promise<Allergy> => {
    if (useMockService()) {
      return mockUpdate(id, data)
    }
    return httpUpdate(id, data)
  },

  /**
   * Deactivate an allergy, capturing a clinical reason.
   */
  deactivate: async (id: string, reason: string): Promise<Allergy> => {
    if (useMockService()) {
      return mockDeactivate(id, reason)
    }
    return httpDeactivate(id, reason)
  },

  /**
   * Set or clear NKDA (No Known Drug Allergies) for a patient.
   *
   * Returns the NKDA record after the operation (or null if cleared).
   */
  setNKDA: async (patientId: string, flag: boolean, reason?: string): Promise<NkdaResult> => {
    if (useMockService()) {
      return mockSetNkda(patientId, flag, reason)
    }
    return httpSetNkda(patientId, flag, reason)
  },
}

